import { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, Plus, Search, Unlink } from 'lucide-react';
import type { Audience } from '@epasu/core';
import { crm, type ContactRow, type OrganizationRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OrganizationDialog } from '@/components/OrganizationDialog';

interface Props {
  contact: ContactRow;
  audiences: Audience[];
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Die Firma eines Kontakts setzen, wechseln oder lösen.
 *
 * Das Gegenstück zu `AssignContactDialog`: dort ist die Firma fest und die
 * Person wird gesucht, hier umgekehrt.
 *
 * Angeboten werden nur Firmen der Zielgruppe des Kontakts — eine Zuordnung
 * darüber hinweg lehnt der Server ab, und was nicht geht, soll nicht zur
 * Auswahl stehen. Fehlt die Firma noch, legt „Neu anlegen“ sie hier an, statt
 * den Weg über die Firmenseite zu erzwingen.
 */
export function SetOrganizationDialog({
  contact,
  audiences,
  onClose,
  onChanged,
}: Props) {
  const [suche, setSuche] = useState('');
  const [firmen, setFirmen] = useState<OrganizationRow[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [neueFirma, setNeueFirma] = useState(false);
  const feldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    feldRef.current?.focus();
  }, []);

  useEffect(() => {
    // Solange der Firmen-Dialog oben liegt, gehört Escape ihm — sonst würden
    // beide Fenster auf einen Tastendruck zugehen.
    if (neueFirma) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, neueFirma, onClose]);

  const laden = useCallback(async () => {
    try {
      setFirmen(
        await crm.organizations.list({
          audienceId: contact.audienceId,
          ...(suche.trim() ? { search: suche.trim() } : {}),
          limit: 50,
        }),
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
    }
    setLaedt(false);
  }, [contact.audienceId, suche]);

  const erster = useRef(true);
  useEffect(() => {
    if (erster.current) {
      erster.current = false;
      void laden();
      return;
    }
    const t = setTimeout(() => void laden(), 250);
    return () => clearTimeout(t);
  }, [laden]);

  const setzen = async (organizationId: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await crm.contacts.setOrganization(contact.id, organizationId);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zuordnen fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Firma des Kontakts wählen"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !busy) onClose();
        }}
      >
        <div className="my-auto w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
          <h2 className="text-lg font-semibold">Firma von {contact.displayName}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {contact.organizationName
              ? `Hängt derzeit an ${contact.organizationName}.`
              : 'Derzeit keiner Firma zugeordnet.'}
          </p>

          <div className="mt-4 flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={feldRef}
                className="pl-9"
                placeholder="Firma suchen …"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                aria-label="Firmen durchsuchen"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setNeueFirma(true)}
              disabled={busy}
            >
              <Plus className="mr-2 h-4 w-4" />
              Neu anlegen
            </Button>
          </div>

          {error && (
            <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="mt-3 max-h-72 overflow-y-auto scrollbar-thin rounded-md border">
            {laedt ? (
              <p className="p-4 text-sm text-muted-foreground">Lade …</p>
            ) : firmen.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">
                {suche.trim()
                  ? 'Keine Firma passt zur Suche. Über „Neu anlegen“ lässt sie sich anlegen.'
                  : 'Für diese Zielgruppe gibt es noch keine Firma.'}
              </p>
            ) : (
              <ul className="divide-y">
                {firmen.map((o) => {
                  const aktuell = o.id === contact.organizationId;
                  return (
                    <li key={o.id}>
                      <button
                        type="button"
                        disabled={busy || aktuell}
                        onClick={() => void setzen(o.id)}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/40 disabled:opacity-60"
                      >
                        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{o.name}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {[o.city, o.domain].filter(Boolean).join(' · ') || '—'}
                          </p>
                        </div>
                        {aktuell && (
                          <span className="shrink-0 text-xs text-muted-foreground">
                            aktuell
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="mt-6 flex justify-between gap-2">
            {contact.organizationId ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => void setzen(null)}
                disabled={busy}
              >
                <Unlink className="mr-2 h-4 w-4" />
                Von der Firma lösen
              </Button>
            ) : (
              <span />
            )}
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Abbrechen
            </Button>
          </div>
        </div>
      </div>

      {neueFirma && (
        <OrganizationDialog
          organization={null}
          audiences={audiences}
          lockedAudienceId={contact.audienceId}
          onClose={() => setNeueFirma(false)}
          onSaved={(firma) => {
            setNeueFirma(false);
            // Direkt zuordnen: wer die Firma hier anlegt, will den Kontakt
            // daran hängen — ein zweiter Klick wäre reine Zeremonie.
            void setzen(firma.id);
          }}
        />
      )}
    </>
  );
}
